// Astrea Ops - Statement of Work PDF (v7.109, 23 Oct 2026).
//
// Martin, 23 Oct 2026: "I want to work on the SOW form so that it works properly. This requires
// using the new attached form, and then being able to add or remove features from the SOW page, as
// well as edit the dates and number of RMs attending" - and a PDF, not a Word file. The SOW tab
// (App.jsx, SowTab) decides the words; this file lays them out like SOW_Template_19Aug26.docx: the
// title and logo, the blue Client / Project header with the services line, price and terms, the
// proposal paragraphs and notes, the numbered scope table, the standard reimbursable expenses and
// the two signature blocks. US Letter, Helvetica (nothing embedded), body text justified, page
// numbers at the foot. Built in the browser with no library, like the Event Form PDF.
import { pdfText, wrapText, widthOf, jpegInfo } from './eventFormPdf';

const NAVY = '#2e4d7b';
const BAND = '#d4dfee';
const PALE = '#dae9f8';
const INK = '#111111';
const GREY = '#555555';
const RULE = '#7f7f7f';

const esc = (s) => {
  let o = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c === 40 || c === 41 || c === 92) o += '\\' + s[i];
    else if (c < 32 || c > 126) o += '\\' + c.toString(8).padStart(3, '0');
    else o += s[i];
  }
  return o;
};
const num = (n) => (Math.round(n * 1000) / 1000).toString();
const rgb = (hex) => {
  const h = String(hex || '#000000').replace('#', '');
  return [0, 2, 4].map((i) => num(parseInt(h.slice(i, i + 2), 16) / 255)).join(' ');
};

// Greedy wrap where the first line is narrower (room for a bold label such as "Note 1:").
function wrapFirst(s, bold, size, w, firstW) {
  const lines = [];
  let cur = '';
  let max = firstW;
  String(s || '').split(/ +/).forEach((word) => {
    const cand = cur ? cur + ' ' + word : word;
    if (widthOf(cand, bold, size) <= max || !cur) { cur = cand; return; }
    lines.push(cur);
    cur = word;
    max = w;
  });
  if (cur || !lines.length) lines.push(cur);
  return lines;
}

// doc: {logo:{bytes}, title, client, date, version, project, serviceLine, price, terms, paras:[text],
//       notes:[{label, text}], scopeTitle, items:[text], expTitle, expIntro, expenses:[text],
//       clientSign, astreaSign, fileTitle}
export function buildSowPdf(doc) {
  const PW = 612, PH = 792, ML = 54, MR = 54, MT = 46, MB = 58;
  const CW = PW - ML - MR;
  const bottom = PH - MB;
  const pages = [];
  let ops = null;
  let y = 0;
  const Y = (top) => PH - top;
  const logo = doc.logo && doc.logo.bytes ? { bytes: doc.logo.bytes, info: jpegInfo(doc.logo.bytes) } : null;
  const hasLogo = !!(logo && logo.info);

  const text = (x, top, s, { bold = false, size = 9.5, color = INK, tw = 0 } = {}) => {
    ops.push('BT /' + (bold ? 'F2' : 'F1') + ' ' + num(size) + ' Tf ' + rgb(color) + ' rg ' + num(tw) + ' Tw ' + num(x) + ' ' + num(Y(top)) + ' Td (' + esc(s) + ') Tj ET');
  };
  const textRight = (xr, top, s, o = {}) => text(xr - widthOf(s, !!o.bold, o.size || 9.5), top, s, o);
  const rect = (x, top, w, h, { fill = null, stroke = null, lw = 0.5 } = {}) => {
    if (!fill && !stroke) return;
    let o = '';
    if (fill) o += rgb(fill) + ' rg ';
    if (stroke) o += rgb(stroke) + ' RG ' + num(lw) + ' w ';
    o += num(x) + ' ' + num(Y(top + h)) + ' ' + num(w) + ' ' + num(h) + ' re ' + (fill && stroke ? 'B' : fill ? 'f' : 'S');
    ops.push(o);
  };
  const hline = (x1, x2, top, color = RULE, lw = 0.6) => ops.push(rgb(color) + ' RG ' + num(lw) + ' w ' + num(x1) + ' ' + num(Y(top)) + ' m ' + num(x2) + ' ' + num(Y(top)) + ' l S');

  const LOGO_H = 39;
  const logoW = hasLogo ? (LOGO_H * logo.info.width) / logo.info.height : 0;
  const newPage = () => {
    ops = [];
    pages.push(ops);
    if (hasLogo) ops.push('q ' + num(logoW) + ' 0 0 ' + LOGO_H + ' ' + num(PW - MR - logoW) + ' ' + num(Y(MT + LOGO_H)) + ' cm /Im1 Do Q');
    y = MT + LOGO_H + 16;
  };
  const room = () => bottom - y;

  // A paragraph, justified except its last line; an optional bold label runs into the first line.
  const para = (s, { size = 9.5, lh = 12.4, x = ML, w = CW, justify = true, label = '', color = INK, gap = 7 } = {}) => {
    const body = pdfText(s);
    const lab = label ? pdfText(label) + ' ' : '';
    const labW = lab ? widthOf(lab, true, size) : 0;
    const lines = lab ? wrapFirst(body, false, size, w, w - labW) : wrapText(body, false, size, w);
    lines.forEach((l, i) => {
      if (y + lh > bottom) newPage();
      const lx = x + (i === 0 ? labW : 0);
      const lw = w - (i === 0 ? labW : 0);
      if (i === 0 && lab) text(x, y + size, lab.trim(), { bold: true, size, color });
      const spaces = (l.match(/ /g) || []).length;
      const extra = justify && i < lines.length - 1 && spaces ? (lw - widthOf(l, false, size)) / spaces : 0;
      text(lx, y + size, l, { size, color, tw: extra > 0 && extra < size ? extra : 0 });
      y += lh;
    });
    y += gap;
  };

  newPage();
  // -- page 1: title, header box, the proposal --
  text(ML, MT + 22, pdfText(doc.title || 'Statement of Work'), { bold: true, size: 16 });
  y = MT + LOGO_H + 14;
  const PAD = 5;
  // Client / Date
  const r1 = 17;
  rect(ML, y, CW, r1, { fill: NAVY, stroke: NAVY });
  const dateW = 150;
  text(ML + PAD, y + 12, 'Client:', { bold: true, size: 9.5, color: '#ffffff' });
  text(ML + PAD + widthOf('Client: ', true, 9.5), y + 12, wrapText(pdfText(doc.client || ''), false, 9.5, CW - dateW - 60)[0] || '', { size: 9.5, color: '#ffffff' });
  const dl = 'Date: ';
  text(ML + CW - dateW, y + 12, dl.trim(), { bold: true, size: 9.5, color: '#ffffff' });
  text(ML + CW - dateW + widthOf(dl, true, 9.5), y + 12, pdfText(doc.date || ''), { size: 9.5, color: '#ffffff' });
  y += r1;
  // Project; v7.115: a revised SOW's version under the date ("Version: 2")
  const ver = pdfText(doc.version || '');
  const projLines = wrapFirst(pdfText(doc.project || ''), false, 9.5, CW - 2 * PAD, (ver ? CW - dateW - 20 - PAD : CW - 2 * PAD) - widthOf('Project: ', true, 9.5));
  const r2 = 6 + projLines.length * 11.5;
  rect(ML, y, CW, r2, { fill: NAVY, stroke: NAVY });
  text(ML + PAD, y + 12, 'Project:', { bold: true, size: 9.5, color: '#ffffff' });
  if (ver) {
    const vl = 'Version: ';
    text(ML + CW - dateW, y + 12, vl.trim(), { bold: true, size: 9.5, color: '#ffffff' });
    text(ML + CW - dateW + widthOf(vl, true, 9.5), y + 12, ver, { size: 9.5, color: '#ffffff' });
  }
  projLines.forEach((l, i) => text(ML + PAD + (i ? 0 : widthOf('Project: ', true, 9.5)), y + 12 + i * 11.5, l, { size: 9.5, color: '#ffffff' }));
  y += r2;
  // Services line and price
  const priceW = 120, bandW = 26;
  const svcLines = wrapText(pdfText(doc.serviceLine || ''), false, 9.5, CW - priceW - bandW - 2 * PAD);
  const r3 = Math.max(20, 8 + svcLines.length * 11.5);
  rect(ML, y, CW, r3, { fill: '#ffffff', stroke: NAVY });
  rect(ML + CW - priceW - bandW, y, bandW, r3, { fill: PALE, stroke: NAVY });
  svcLines.forEach((l, i) => text(ML + PAD, y + 13 + i * 11.5, l, { size: 9.5 }));
  textRight(ML + CW - PAD, y + 13.5, pdfText(doc.price || ''), { bold: true, size: 10.5 });
  y += r3;
  // Terms
  const tLines = wrapText(pdfText(doc.terms || ''), false, 8.5, CW - 2 * PAD);
  const r4 = 8 + tLines.length * 10.6;
  rect(ML, y, CW, r4, { fill: BAND, stroke: NAVY });
  tLines.forEach((l, i) => {
    const spaces = (l.match(/ /g) || []).length;
    const extra = i < tLines.length - 1 && spaces ? (CW - 2 * PAD - widthOf(l, false, 8.5)) / spaces : 0;
    text(ML + PAD, y + 11.5 + i * 10.6, l, { size: 8.5, tw: extra > 0 && extra < 8.5 ? extra : 0 });
  });
  y += r4 + 16;
  (doc.paras || []).filter((s) => String(s || '').trim()).forEach((s) => para(s));
  (doc.notes || []).filter((n) => n && String(n.text || '').trim()).forEach((n) => para(n.text, { label: n.label, size: 9, lh: 11.6 }));

  // -- scope of work: numbered table --
  // v7.110 (Martin, 23 Oct 2026): "Can you reduce the font size on the SOW 20 item list please to fit
  // it into one page?" Smaller type (8.5 pt), and if the items still run past the page (long or added
  // items) the size steps down until they fit, to 7 pt at the smallest.
  newPage();
  const numW = 30;
  let FS = 8.5, LH = 10.2, ROW_PAD = 3;
  const items = (doc.items || []).map((it) => pdfText(it));
  const tableH = (fs, lh, pad) => 17 + items.reduce((a, it) => a + 2 * pad + wrapText(it, false, fs, CW - numW - 2 * pad).length * lh, 0);
  while (FS > 7 && y + tableH(FS, LH, ROW_PAD) > bottom) {
    FS = Math.round((FS - 0.25) * 100) / 100;
    LH = FS * 1.2;
    ROW_PAD = FS < 8 ? 2.5 : 3;
  }
  const head = () => {
    rect(ML, y, CW, 17, { fill: BAND, stroke: RULE });
    text(ML + PAD, y + 12, pdfText(doc.scopeTitle || 'Scope of Work'), { bold: true, size: 10 });
    y += 17;
  };
  head();
  items.forEach((it, i) => {
    const lines = wrapText(it, false, FS, CW - numW - 2 * ROW_PAD);
    const h = 2 * ROW_PAD + lines.length * LH;
    if (y + h > bottom) { newPage(); head(); }
    rect(ML, y, numW, h, { fill: '#ffffff', stroke: RULE });
    rect(ML + numW, y, CW - numW, h, { fill: '#ffffff', stroke: RULE });
    const nTxt = String(i + 1);
    text(ML + (numW - widthOf(nTxt, false, FS)) / 2, y + ROW_PAD + FS, nTxt, { size: FS });
    lines.forEach((l, j) => text(ML + numW + ROW_PAD, y + ROW_PAD + FS + j * LH, l, { size: FS }));
    y += h;
  });

  // -- standard reimbursable expenses --
  newPage();
  text(ML, y + 11, pdfText(doc.expTitle || 'Standard Reimbursable Expenses'), { bold: true, size: 11 });
  y += 22;
  if (doc.expIntro) para(doc.expIntro, { gap: 9 });
  (doc.expenses || []).forEach((s, i) => {
    const n = (i + 1) + '.';
    const ind = 20;
    const lines = wrapText(pdfText(s), false, 9.5, CW - ind);
    if (y + lines.length * 12.4 > bottom) newPage();
    text(ML, y + 9.5, n, { size: 9.5 });
    lines.forEach((l, j) => {
      const spaces = (l.match(/ /g) || []).length;
      const extra = j < lines.length - 1 && spaces ? (CW - ind - widthOf(l, false, 9.5)) / spaces : 0;
      text(ML + ind, y + 9.5, l, { size: 9.5, tw: extra > 0 && extra < 9.5 ? extra : 0 });
      y += 12.4;
    });
    y += 5;
  });

  // -- signatures --
  if (room() < 170) newPage(); else y += 26;
  const colW = (CW - 30) / 2;
  const block = (x, who) => {
    let yy = y;
    wrapText(pdfText(who), true, 10, colW).forEach((l) => { text(x, yy + 10, l, { bold: true, size: 10 }); yy += 13; });
    yy += 14;
    ['Signature:', 'Name:', 'Title:', 'Date:'].forEach((l) => {
      text(x, yy + 9.5, l, { size: 9.5 });
      hline(x + 62, x + colW, yy + 11, INK, 0.6);
      yy += 30;
    });
    return yy;
  };
  const a = block(ML, doc.clientSign || 'Client');
  const b = block(ML + colW + 30, doc.astreaSign || 'Astrea Services, Inc.');
  y = Math.max(a, b);

  // page numbers
  pages.forEach((p, i) => {
    ops = p;
    const s = String(i + 1);
    text((PW - widthOf(s, false, 8.5)) / 2, PH - MB + 30, s, { size: 8.5, color: GREY });
  });

  // -- assemble the file --
  const enc = (s) => { const u = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i) & 255; return u; };
  const chunks = [];
  let len = 0;
  const offsets = [];
  const add = (u) => { chunks.push(u); len += u.length; };
  const obj = (n, body) => { offsets[n] = len; add(enc(n + ' 0 obj\n')); (Array.isArray(body) ? body : [body]).forEach((bb) => add(typeof bb === 'string' ? enc(bb) : bb)); add(enc('\nendobj\n')); };
  add(enc('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n'));
  const nPages = pages.length;
  const firstPageObj = hasLogo ? 7 : 6;
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  obj(2, '<< /Type /Pages /Kids [' + pages.map((_, i) => (firstPageObj + i * 2) + ' 0 R').join(' ') + '] /Count ' + nPages + ' >>');
  obj(3, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  obj(4, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
  const d = new Date();
  const pd = (n) => String(n).padStart(2, '0');
  const stamp = 'D:' + d.getUTCFullYear() + pd(d.getUTCMonth() + 1) + pd(d.getUTCDate()) + pd(d.getUTCHours()) + pd(d.getUTCMinutes()) + pd(d.getUTCSeconds()) + 'Z';
  obj(5, '<< /Title (' + esc(pdfText(doc.fileTitle || doc.title || 'Statement of Work')) + ') /Author (Astrea Services, Inc.) /Producer (Astrea Ops) /CreationDate (' + stamp + ') >>');
  if (hasLogo) {
    const cs = logo.info.components === 1 ? '/DeviceGray' : logo.info.components === 4 ? '/DeviceCMYK' : '/DeviceRGB';
    obj(6, ['<< /Type /XObject /Subtype /Image /Width ' + logo.info.width + ' /Height ' + logo.info.height + ' /ColorSpace ' + cs + ' /BitsPerComponent 8 /Filter /DCTDecode' + (logo.info.components === 4 ? ' /Decode [1 0 1 0 1 0 1 0]' : '') + ' /Length ' + logo.bytes.length + ' >>\nstream\n', logo.bytes, '\nendstream']);
  }
  const res = '<< /Font << /F1 3 0 R /F2 4 0 R >>' + (hasLogo ? ' /XObject << /Im1 6 0 R >>' : '') + ' >>';
  pages.forEach((p, i) => {
    const pn = firstPageObj + i * 2;
    const content = p.join('\n');
    obj(pn, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + PW + ' ' + PH + '] /Resources ' + res + ' /Contents ' + (pn + 1) + ' 0 R >>');
    obj(pn + 1, '<< /Length ' + content.length + ' >>\nstream\n' + content + '\nendstream');
  });
  const nObj = firstPageObj + nPages * 2;
  const xref = len;
  let x = 'xref\n0 ' + nObj + '\n0000000000 65535 f \n';
  for (let i = 1; i < nObj; i++) x += String(offsets[i]).padStart(10, '0') + ' 00000 n \n';
  x += 'trailer\n<< /Size ' + nObj + ' /Root 1 0 R /Info 5 0 R >>\nstartxref\n' + xref + '\n%%EOF\n';
  add(enc(x));
  const out = new Uint8Array(len);
  let o = 0;
  chunks.forEach((c) => { out.set(c, o); o += c.length; });
  return out;
}
